import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, take, takeUntil, tap } from 'rxjs/operators';

import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { ProfileHubSettingsDialogService } from '@main-client/src/app/profile/profile-hub-settings-dialog/profile-hub-settings-dialog.service';

import { rotate90 } from '@libs/src/animations/animations';
import { Memoize } from '@libs/src/functions/memoize';
import {
  insightPreferencesOptions,
  showLegacyGoals,
} from '@libs/src/goal/account-goal.helper';
import { AccountGoal } from '@libs/src/models/account-goal.model';
import {
  Account,
  AccountGroupMembership,
  AccountSegmentSelection,
} from '@libs/src/models/account.model';
import { Group, GroupCustomSegment } from '@libs/src/models/group.model';
import {
  getHiddenSegmentsVisibleToUser,
  getSegmentationSelection,
} from '@libs/src/segment-selections/segment-selections.helper';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import { IAppState } from '@main-client/src/app/app.state';
import { Profile } from '@main-client/src/app/profile/profile';
import { isAdminOfGroup } from '@main-client/src/app/user/user.helpers';

import find from 'lodash-es/find';
import isEmpty from 'lodash-es/isEmpty';

@Component({
  animations: [rotate90],
  providers: [DestroySubscriptionsService],
  selector: 'app-profile-more-info',
  templateUrl: './profile-more-info.component.html',
})
export class ProfileMoreInfoComponent implements OnInit {
  @Input() isUser: boolean;
  @Input() profile: Profile;
  @Input() user: Account;
  tenantState = this.store.select('tenant').pipe(
    filter((tenant: Tenant) => !isEmpty(tenant)),
    take(1),
  );
  interactions: Array<{
    fieldName: keyof AccountGroupMembership;
    text: string;
  }> = [
    {
      fieldName: 'inviteable',
      text: 'Can be messaged by other members',
    },
    {
      fieldName: 'opted_into_matchmaking',
      text: 'Can be introduced to other members automatically',
    },
    {
      fieldName: 'searchable',
      text: 'Can be seen by other members',
    },
  ];

  constructor(
    private readonly destroy$: DestroySubscriptionsService,
    public readonly membershipDialog: ProfileHubSettingsDialogService,
    public readonly route: ActivatedRoute,
    public readonly router: Router,
    private readonly store: Store<IAppState>,
  ) {}

  ngOnInit() {
    this.tenantState
      .pipe(
        tap((tenant) => {
          if (!tenant.enabled_features.conversations) {
            this.interactions = this.interactions.filter(
              (interaction) => 'inviteable' !== interaction.fieldName,
            );
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  @Memoize()
  getGroup({
    groupSlug,
    profile,
  }: {
    groupSlug: string;
    profile: Profile;
  }): AccountGroupMembership {
    return profile?.groups?.find(
      (membership) => membership?.document?.slug === groupSlug,
    );
  }

  getSelection(
    selections: AccountSegmentSelection[],
    segment: GroupCustomSegment,
  ): AccountSegmentSelection {
    return getSegmentationSelection(selections, segment);
  }

  openDialog(membership: AccountGroupMembership) {
    return this.membershipDialog
      .openDialog({
        membership,
        group: membership.document,
        isIntroOnly: false,
        profile: this.profile,
        user: this.user,
      })
      .afterClosed()
      .pipe(
        filter((isRemoved) => isRemoved),
        tap(() => {
          const profileSlug = this.route.snapshot.paramMap.get('profileSlug');
          this.router.navigate(['/profile', profileSlug]);
        }),
      )
      .subscribe();
  }

  @Memoize()
  getGoalInsightPreferencesText(goal: AccountGoal) {
    const insightsPreference = find(insightPreferencesOptions, {
      value: goal.insight_preferences,
    });
    return insightsPreference?.value?.provide &&
      insightsPreference?.value?.receive
      ? $localize`:@@profileMoreInfo.provideAndReceiveInsights:Both learn and offer knowledge`
      : insightsPreference?.name;
  }

  @Memoize()
  getGoalPanelData(goal: AccountGoal) {
    return { ...goal, isExpanded: false };
  }

  @Memoize()
  showLegacyGoals(membership: AccountGroupMembership) {
    return showLegacyGoals(membership);
  }

  @Memoize()
  canDisplayPrivateSegments({
    account,
    group,
    user,
  }: {
    account: Account;
    group: Group;
    user: Account;
  }) {
    const hiddenSegmentsToDisplay = getHiddenSegmentsVisibleToUser(
      group,
      user,
      account,
    );
    const isUserAdmin = user.isAdmin || isAdminOfGroup(user, group);
    return (this.user || isUserAdmin) && !!hiddenSegmentsToDisplay?.length;
  }
}
